/**
 * JWT token utilities
 */

import { jwtDecode } from 'jwt-decode';
import type { DecodedToken } from '../types/auth';

export interface TokenUtils {
  getAccessToken: () => string | null;
  setAccessToken: (token: string) => void;
  getRefreshToken: () => string | null;
  setRefreshToken: (token: string) => void;
  getTenantSlug: () => string | null;
  setTenantSlug: (slug: string) => void;
  clearTokens: () => void;
}

/**
 * Create portal-specific token utilities with prefixed localStorage keys.
 * This prevents token collisions when multiple portals share the same origin.
 */
export function createTokenUtils(prefix: string): TokenUtils {
  const ACCESS_TOKEN_KEY = `${prefix}_access_token`;
  const REFRESH_TOKEN_KEY = `${prefix}_refresh_token`;
  const TENANT_SLUG_KEY = `${prefix}_tenant_slug`;

  return {
    getAccessToken: () => localStorage.getItem(ACCESS_TOKEN_KEY),
    setAccessToken: (token: string) => localStorage.setItem(ACCESS_TOKEN_KEY, token),
    getRefreshToken: () => localStorage.getItem(REFRESH_TOKEN_KEY),
    setRefreshToken: (token: string) => localStorage.setItem(REFRESH_TOKEN_KEY, token),
    getTenantSlug: () => localStorage.getItem(TENANT_SLUG_KEY),
    setTenantSlug: (slug: string) => localStorage.setItem(TENANT_SLUG_KEY, slug),
    clearTokens: () => {
      localStorage.removeItem(ACCESS_TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      localStorage.removeItem(TENANT_SLUG_KEY);
    },
  };
}

// Default instances for backward compatibility
const tenantTokens = createTokenUtils('intellimon_tenant');
const managerTokens = createTokenUtils('intellimon_manager');

export const getAccessToken = tenantTokens.getAccessToken;
export const setAccessToken = tenantTokens.setAccessToken;
export const getRefreshToken = tenantTokens.getRefreshToken;
export const setRefreshToken = tenantTokens.setRefreshToken;
export const getTenantSlug = tenantTokens.getTenantSlug;
export const setTenantSlug = tenantTokens.setTenantSlug;
export const clearTokens = tenantTokens.clearTokens;

export { managerTokens, tenantTokens };

export function decodeToken(token: string): DecodedToken | null {
  try {
    return jwtDecode<DecodedToken>(token);
  } catch {
    return null;
  }
}

export function isTokenExpired(token: string, bufferSeconds = 60): boolean {
  const decoded = decodeToken(token);
  if (!decoded) return true;

  const currentTime = Math.floor(Date.now() / 1000);
  return decoded.exp < currentTime + bufferSeconds;
}

export function getTokenRole(token: string): string | null {
  const decoded = decodeToken(token);
  return decoded?.role ?? null;
}

export function getTokenSubject(token: string): string | null {
  const decoded = decodeToken(token);
  return decoded?.sub ?? null;
}
